import { STATUS_ROLES } from './constants';

/**
 * itemToString - returns the string to display for an item in the typeahead
 * @param {Object|String} item either a string or an object with a `toDisplay` property
 * @returns {String} the string to display
 */
export const itemToString = item => {
  return typeof item === 'string' ? item : item?.toDisplay || '';
};

const formatCount = (count, inputValue) => {
  if (count === 0) return '';
  const noun = count === 1 ? 'result' : 'results';
  return inputValue
    ? `${count} ${noun} for ${inputValue}`
    : `${count} ${noun} available, use up and down arrow keys to navigate`;
};

export function buildAnnouncementMessage(
  items,
  toString,
  inputValue,
  { isLoading = false, loadingMessage = '' } = {},
) {
  // While loading, announce the loading message instead of the (likely stale)
  // option list. The dropdown's visible status row uses the same string.
  if (isLoading && loadingMessage) {
    return loadingMessage;
  }

  const [statusItems, selectableItems, disabledNonStatusItems] = items.reduce(
    (acc, item) => {
      if (STATUS_ROLES.includes(item?.statusRole)) {
        return [[...acc[0], item], acc[1], acc[2]];
      }
      if (item?.disabled) {
        return [acc[0], acc[1], [...acc[2], item]];
      }
      return [acc[0], [...acc[1], item], acc[2]];
    },
    [[], [], []],
  );

  const noMatchItem = statusItems.find(
    item => item?.id === 'no-matching-services',
  );
  if (noMatchItem) {
    // No matching services pairs with a fallback selectable item that isn't
    // tied to the search term, so don't fall through to the count message.
    // Use the item's own display text so callers control the wording.
    const noMatchText = toString(noMatchItem);
    return inputValue ? `${noMatchText} for ${inputValue}` : noMatchText;
  }

  const statusText = statusItems.map(toString).join('. ');

  // Disabled items without a statusRole still appear in the visible status
  // section — fold their text into the announcement so SR users hear the same
  // information sighted users see.
  const disabledText = disabledNonStatusItems.map(toString).join('. ');

  const selectableCount = selectableItems.length;
  const countText = formatCount(selectableCount, inputValue);
  const parts = [statusText, disabledText, countText].filter(Boolean);
  return parts.length ? parts.join('. ') : 'No results found';
}

/**
 * isNoResultsAnnouncement - determines whether the current options represent a
 * "no results" state that should be announced assertively (so it interrupts the
 * combobox's queued semantic announcements) rather than politely (a11y #142577).
 *
 * Limited to explicit no-results signals: an empty list, or a status row with
 * id "no-matching-services" (which pairs with a non-matching fallback
 * option). Informational status-only lists (e.g. "Enter at least 3 characters")
 * deliberately stay polite so they don't interrupt the user.
 *
 * @param {Array} items the current option list
 * @param {Object} [opts]
 * @param {Boolean} [opts.isLoading] whether the list is still loading
 * @returns {Boolean} true when the announcement should be assertive
 */
export function isNoResultsAnnouncement(items, { isLoading = false } = {}) {
  if (isLoading) {
    return false;
  }

  const list = items || [];
  const hasNoMatchStatus = list.some(
    item => item?.id === 'no-matching-services',
  );

  return list.length === 0 || hasNoMatchStatus;
}
